import React, { useEffect } from 'react'
import { gql, useQuery } from '@apollo/client'
import { useHistory, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Alert, Space } from 'antd'
import dayjs from 'dayjs'

import Layout from '../App/layout'
import LayoutContentWrapper from '../../components/utility/layoutWrapper'
import ContentLayout from '../../components/utility/layoutContent'

import Filters from './Filters'
import LineItemsTable from './Table'
import ExportButton from './ExportButton'
import { DATE_FORMAT, DEFAULT_ORDER_STATES, ORDER_STATES, PAGE_SIZE, SORTS } from './constants'

const LINE_ITEMS_SEARCH_QUERY = gql`
  query LineItemsSearch(
    $first: Int
    $after: String
    $query: String
    $itemId: ID
    $provinceId: ID
    $districtId: ID
    $orderStates: [String!]
    $requestItemsOnly: Boolean
    $paidFrom: ISO8601Date
    $paidTo: ISO8601Date
    $sort: LineItemsSearchSortEnum
  ) {
    lineItemsSearch(
      first: $first
      after: $after
      query: $query
      itemId: $itemId
      provinceId: $provinceId
      districtId: $districtId
      orderStates: $orderStates
      requestItemsOnly: $requestItemsOnly
      paidFrom: $paidFrom
      paidTo: $paidTo
      sort: $sort
    ) {
      totalCount
      nodes {
        id
        name
        quantity
        unit
        isRequestItem
        cost {
          display
        }
        singlePrice {
          display
        }
        updatedAt {
          raw
        }
        orderNumber
        orderState
        paymentTime {
          raw
        }
        customerNo
        customerName
        customerPhoneNumber
        shippingDistrict
        shippingProvince
        supplierNames
        productCategoryName
        truckOriginAddresses
      }
    }
  }
`

const ITEM_BY_EXTERNAL_ID_QUERY = gql`
  query LineItemsFilterItem($itemId: String!) {
    item(itemId: $itemId) {
      id
      itemId
      name
    }
  }
`

const offsetCursor = (page) => (page > 1 ? btoa(String((page - 1) * PAGE_SIZE)).replace(/=+$/, '') : null)

const parseDate = (value) => {
  const date = value ? dayjs(value, DATE_FORMAT, true) : null
  return date && date.isValid() ? date.format(DATE_FORMAT) : undefined
}

const parsePage = (value) => {
  const page = parseInt(value, 10)
  return page > 1 ? page : 1
}

const parseFilters = (search) => {
  const params = new URLSearchParams(search)
  const orderStates = (params.get('order_states') || '')
    .split(',')
    .filter((state) => ORDER_STATES.includes(state))
  const sort = params.get('sort')

  return {
    query: params.get('query') || '',
    item: params.get('item') || '',
    provinceId: params.get('province_id') || undefined,
    districtId: params.get('district_id') || undefined,
    orderStates: orderStates.length ? orderStates : DEFAULT_ORDER_STATES,
    requestItemsOnly: params.get('request_items_only') === 'true',
    paidFrom: parseDate(params.get('paid_from')),
    paidTo: parseDate(params.get('paid_to')),
    sort: SORTS.includes(sort) ? sort : SORTS[0],
    page: parsePage(params.get('page'))
  }
}

const isDefaultOrderStates = (orderStates) => (
  orderStates.length === DEFAULT_ORDER_STATES.length &&
  DEFAULT_ORDER_STATES.every((state) => orderStates.includes(state))
)

const buildSearch = (filters) => {
  const params = new URLSearchParams()
  if (filters.query) params.set('query', filters.query)
  if (filters.item) params.set('item', filters.item)
  if (filters.provinceId) params.set('province_id', filters.provinceId)
  if (filters.districtId) params.set('district_id', filters.districtId)
  if (filters.orderStates && filters.orderStates.length && !isDefaultOrderStates(filters.orderStates)) {
    params.set('order_states', filters.orderStates.join(','))
  }
  if (filters.requestItemsOnly) params.set('request_items_only', 'true')
  if (filters.paidFrom) params.set('paid_from', filters.paidFrom)
  if (filters.paidTo) params.set('paid_to', filters.paidTo)
  if (filters.sort && filters.sort !== SORTS[0]) params.set('sort', filters.sort)
  if (filters.page && filters.page > 1) params.set('page', String(filters.page))
  const search = params.toString()
  return search ? `?${search}` : ''
}

const LineItemsIndexPage = () => {
  const { t, i18n } = useTranslation(['lineItems', 'common'])
  const history = useHistory()
  const location = useLocation()
  const filters = parseFilters(location.search)

  useEffect(() => {
    document.title = `${i18n.t('common:lineItems')} | OSH CRM`
  }, [i18n.language])

  const itemLookup = useQuery(ITEM_BY_EXTERNAL_ID_QUERY, {
    variables: { itemId: filters.item },
    skip: !filters.item
  })
  const resolvedItem = filters.item && itemLookup.data ? itemLookup.data.item : null
  const itemNotFound = Boolean(filters.item) && !itemLookup.loading && !itemLookup.error && !resolvedItem
  const waitingForItem = Boolean(filters.item) && !resolvedItem

  const { data, loading, error } = useQuery(LINE_ITEMS_SEARCH_QUERY, {
    variables: {
      first: PAGE_SIZE,
      after: offsetCursor(filters.page),
      query: filters.query || undefined,
      itemId: resolvedItem ? resolvedItem.itemId : undefined,
      provinceId: filters.provinceId,
      districtId: filters.districtId,
      orderStates: filters.orderStates,
      requestItemsOnly: filters.requestItemsOnly,
      paidFrom: filters.paidFrom,
      paidTo: filters.paidTo,
      sort: filters.sort.toUpperCase()
    },
    skip: waitingForItem,
    fetchPolicy: 'cache-and-network'
  })

  const navigate = (nextFilters) => {
    history.push({ pathname: location.pathname, search: buildSearch(nextFilters) })
  }

  const connection = (!waitingForItem && data && data.lineItemsSearch) || {}

  return (
    <Layout>
      <LayoutContentWrapper breadcrumbs={[{ path: '/line_items', name: t('common:lineItems') }]}>
        <ContentLayout>
          <Filters
            key={location.search}
            filters={filters}
            resolvedItem={resolvedItem}
            onApply={(nextFilters) => navigate({ ...nextFilters, page: 1 })}
            onClear={() => navigate({})}
          />
          <Space direction='vertical' style={{ width: '100%', marginBottom: 16 }}>
            {itemNotFound && <Alert type='warning' showIcon message={t('lineItems:itemNotFound', { item: filters.item })} />}
            {error && <Alert type='error' showIcon message={error.message} />}
            <ExportButton filters={filters} itemId={resolvedItem ? resolvedItem.itemId : undefined} disabled={waitingForItem} />
          </Space>
          <LineItemsTable
            loading={loading || itemLookup.loading}
            rows={connection.nodes || []}
            page={filters.page}
            pageSize={PAGE_SIZE}
            total={connection.totalCount || 0}
            onPageChange={(page) => navigate({ ...filters, page })}
          />
        </ContentLayout>
      </LayoutContentWrapper>
    </Layout>
  )
}

export default LineItemsIndexPage
